import { useAmountLimits } from "@components/donate/DonateV3/PaymentProcess/useAmountLimits";
import { DonateCustomField } from "@components/donate/DonateV3/PaymentProcess/useDonateCustomFields";
import { DonateFormType } from "@components/donate/DonateV3/types";
import { DonateFormSearchValues } from "@components/donate/DonateV3/useDonateFormSearchParams";
import { outsideBoundsErrorMessage } from "@components/donate/helpers";
import { Big } from "big.js";
import { FieldPath, UseFormSetError } from "react-hook-form";

import { isEmail } from "@every.org/common/src/codecs/contact";
import { CurrencyValue } from "@every.org/common/src/codecs/currency";
import {
  commentTextCodec,
  MAX_COMMENT_LEN,
} from "@every.org/common/src/codecs/text";
import {
  Currency,
  DonationFrequency,
  PaymentMethod,
} from "@every.org/common/src/entity/types";
import { CryptoCurrency } from "@every.org/common/src/entity/types/crypto";
import {
  donationValueIsInvalid,
  validDonationValueOrUndefined,
} from "@every.org/common/src/helpers/donationValue";
import { removeUndefinedOrNullValues } from "@every.org/common/src/helpers/objectUtilities";

import { logger } from "src/utility/logger";
import { LATIN_NAME_REGEX } from "src/utility/userValidators";

// TODO #8478: parametrize currency
function validateCryptoNumber({
  amount,
  shorten,
}: {
  amount: string;
  shorten: boolean;
}): string | undefined {
  let amountBig: Big;
  try {
    amountBig = new Big(amount);
  } catch (e) {
    return shorten
      ? "Please enter an amount"
      : "Please enter an amount to continue";
  }

  return amountBig.lte(0) ? "Amount must be greater than 0" : undefined;
}

// TODO #8478: parametrize currency
export function validateNumber({
  amount,
  shorten,
  minValue,
  maxValue,
}: {
  amount: string;
  minValue: CurrencyValue;
  maxValue: CurrencyValue;
  shorten: boolean;
}): string | undefined {
  let amountBig: Big;
  try {
    amountBig = new Big(amount);
  } catch (e) {
    return shorten
      ? "Please enter an amount"
      : "Please enter an amount to continue";
  }

  // TODO #8478: consider the selected currency
  const valueError = donationValueIsInvalid({
    value: { currency: Currency.USD, amount: amountBig },
    minValue,
    maxValue,
  });
  if (valueError) {
    return outsideBoundsErrorMessage({
      // TODO #8478: consider the selected currency
      currency: Currency.USD,
      maxOrMin: valueError,
      shorten,
      minValue,
      maxValue,
    });
  }

  if (amount.length && !isNaN(Number(amount))) {
    return undefined;
  }
  logger.error({ message: "Error parsing amount value", data: { amount } });
  return "Error parsing amount";
}

export function validateCryptoAmountAndCurrency({
  currency,
  amount,
  shorten,
  setError,
}: {
  currency?: CryptoCurrency;
  amount: string;
  shorten: boolean;
  setError: UseFormSetError<DonateFormType>;
}) {
  const currencyValidationResult = currency
    ? undefined
    : "Please choose a crypto currency";
  const amountValidationResult = validateCryptoNumber({
    amount,
    shorten,
  });
  if (currencyValidationResult) {
    setError("cryptoCurrency", {
      type: "string",
      message: currencyValidationResult,
    });
  }
  if (amountValidationResult) {
    setError("cryptoPledgeAmount", {
      type: "string",
      message: amountValidationResult,
    });
  }
  return !(currencyValidationResult || amountValidationResult);
}

/**
 * Validates amount and frequency values for the form.
 *
 * Returns true if they validate and false if either fails validation.
 */
export function validateAmountAndFrequency({
  frequency,
  amount,
  minValue,
  maxValue,
  shorten,
  setError,
}: {
  frequency?: DonationFrequency;
  amount: string;
  minValue: CurrencyValue;
  maxValue: CurrencyValue;
  shorten: boolean;
  setError: UseFormSetError<DonateFormType>;
}) {
  const frequencyValidationResult = frequency
    ? undefined
    : "Please choose a donation frequency";
  const amountValidationResult = validateNumber({
    amount,
    minValue,
    maxValue,
    shorten,
  });
  if (frequencyValidationResult) {
    setError("frequency", {
      type: "string",
      message: frequencyValidationResult,
    });
  }
  if (amountValidationResult) {
    setError("amount", { type: "string", message: amountValidationResult });
  }
  return !(frequencyValidationResult || amountValidationResult);
}

export function getValidCommentText(text?: string) {
  return text && commentTextCodec.is(text) ? text : undefined;
}

/**
 * Validates amount for credits only donations for the form.
 *
 * Returns true if they validate and false if either fails validation.
 */
export function validateCreditsOnlyAmount({
  amount,
  availableGivingCredit,
  paymentMethod,
  shorten,
  errorMessage = "Amount must be at most your current credits",
  setError,
  setErrorMessage,
}: {
  amount: string;
  availableGivingCredit: Big | undefined;
  paymentMethod: PaymentMethod;
  shorten: boolean;
  errorMessage?: string;
  setError?: UseFormSetError<DonateFormType>;
  setErrorMessage?: ((errorMessage: string | undefined) => void) | undefined;
}) {
  if (paymentMethod !== PaymentMethod.CREDITS) {
    return true;
  }
  let amountBig: Big;
  try {
    amountBig = new Big(amount);
  } catch (e) {
    if (setError) {
      setError("amount", {
        type: "string",
        message: errorMessage,
      });
    }
    if (setErrorMessage) {
      setErrorMessage(errorMessage);
    }
    return false;
  }
  if (!availableGivingCredit || amountBig.gt(availableGivingCredit)) {
    if (setError) {
      setError("amount", {
        type: "string",
        message: errorMessage,
      });
    }
    if (setErrorMessage) {
      setErrorMessage(errorMessage);
    }
    return false;
  }
  return true;
}

export function validateCommentText(
  text: string | undefined,
  setError: UseFormSetError<DonateFormType>,
  errorName: FieldPath<DonateFormType>,
  limitOverride?: number
) {
  if (
    text?.length &&
    (!getValidCommentText(text) ||
      (limitOverride && text.length > limitOverride))
  ) {
    setError(errorName, {
      type: "string",
      message: `Max note length is ${limitOverride || MAX_COMMENT_LEN}.`,
    });
    return false;
  }

  return true;
}

export function validateCustomFieldResponses({
  customFields,
  customFieldResponses,
  setError,
}: {
  customFields?: DonateCustomField[];
  customFieldResponses?: Record<string, string>;
  setError: UseFormSetError<DonateFormType>;
}) {
  if (!customFields?.length) {
    return true;
  }

  let valid = true;

  customFields.forEach((field, index) => {
    const fallbackKey = `customField_${index}`;
    const fieldKey = field.formKey || fallbackKey;
    const fieldResponse =
      customFieldResponses?.[fieldKey] ?? customFieldResponses?.[field.heading];

    if (field.required && !fieldResponse?.trim().length) {
      setError(
        `customFieldResponses.${fieldKey}` as FieldPath<DonateFormType>,
        {
          type: "string",
          message: "This field is required.",
        }
      );
      valid = false;
    }
  });

  return valid;
}

export function useValidValuesFromSearchParams(
  values: DonateFormSearchValues,
  options: { minDonationValue?: CurrencyValue }
) {
  // If there's an amount in the search params, use the new $2 minimum bounds
  const hasAmountQueryParam = values.amount !== undefined;

  // Use currency from query params if available, otherwise default to USD
  const currency =
    values.minValue?.currency ||
    options.minDonationValue?.currency ||
    Currency.USD;

  const [minValue, maxValue] = useAmountLimits(
    values.minValue || options.minDonationValue,
    undefined,
    hasAmountQueryParam
  );
  const validAmount =
    values.amount &&
    !!validDonationValueOrUndefined({
      value: { currency, amount: new Big(values.amount) },
      minValue,
      maxValue,
    })
      ? values.amount
      : undefined;

  const validValues = removeUndefinedOrNullValues({
    ...values,
    amount: validAmount,
  });

  if (values.address === null) {
    return {
      ...validValues,
      address: null,
    };
  }

  return validValues;
}

export function validateStockSymbolAndAmount({
  stockSymbol,
  stockAmount,
  setError,
}: {
  stockSymbol?: string;
  stockAmount?: number;
  setError: UseFormSetError<DonateFormType>;
}) {
  let valid = true;
  if (!stockSymbol || stockSymbol.length === 0) {
    setError("stockSymbol", {
      message: "Please enter a valid stock symbol",
    });
    valid = false;
  }

  if (!stockAmount) {
    setError("stockAmount", {
      message: "Please enter a valid share amount",
    });
    valid = false;
  }

  return valid;
}

export function validateUserEmail(
  email: string | undefined,
  setError: UseFormSetError<DonateFormType>,
  clearErrors?: (name?: keyof DonateFormType) => void
) {
  if (!email || email.length === 0) {
    setError("email", { message: "Please provide a valid email address." });
    return false;
  }
  const validationResult = isEmail(email);

  if (!validationResult) {
    setError("email", { message: "Incorrect email format." });
    return false;
  }

  // Clear the email error when validation succeeds and clearErrors function is provided
  if (clearErrors) {
    clearErrors("email");
  }

  return true;
}

export function validateName(
  value: string | undefined,
  varName: keyof DonateFormType,
  humanVarName: string,
  setError: UseFormSetError<DonateFormType>,
  clearErrors?: (name?: keyof DonateFormType) => void
) {
  if (!value || value.length === 0) {
    setError(varName, {
      message: `Please provide a ${humanVarName}.`,
      type: "required",
    });
    return false;
  }
  if (!LATIN_NAME_REGEX.test(value)) {
    setError(varName, {
      message: "Only latin characters A-Z may be used.",
      type: "pattern",
    });
    return false;
  }
  // Clear the name error when validation succeeds and clearErrors function is provided
  if (clearErrors) {
    clearErrors(varName);
  }

  return true;
}

function validateFirstAndLastName(
  firstName: string | undefined,
  lastName: string | undefined,
  setError: UseFormSetError<DonateFormType>
) {
  let isValid = true;

  if (!firstName?.trim() && !lastName?.trim()) {
    // setting to a first name because there's no field name for both
    // and we want to show a single error message under the both fields
    // empty message for last name to highlight the field in error state
    setError("firstName", { message: "Please provide a first and last name" });
    setError("lastName", { message: "" });

    return false;
  }

  if (!validateName(firstName?.trim(), "firstName", "first name", setError)) {
    isValid = false;
  }

  if (!validateName(lastName?.trim(), "lastName", "last name", setError)) {
    isValid = false;
  }

  return isValid;
}

/**
 * Validates guest user required information (first name, last name, and email).
 * Returns true if all validations pass, false otherwise.
 */
export function validateNameEmail({
  firstName,
  lastName,
  email,
  setError,
}: {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  setError: UseFormSetError<DonateFormType>;
}) {
  let isValid = true;

  if (!validateFirstAndLastName(firstName, lastName, setError)) {
    isValid = false;
  }

  // Validate email
  if (!validateUserEmail(email, setError)) {
    isValid = false;
  }

  return isValid;
}

export function validateLocation(
  location: DonateFormType["location"],
  setError: UseFormSetError<DonateFormType>,
  isRequired: boolean
) {
  if (isRequired && !location) {
    setError("location", { message: "Please provide your address." });
    return false;
  }

  return true;
}
